import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  FactoryOutlined as FactoryIcon,
  CakeOutlined as CakeIcon,
  AssessmentOutlined as ReportIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import { getKitchenReports } from "../../service/orderService";

const getTodayDate = () => new Date();
const getTomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d;
};
const getYesterdayDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};

const formatDateToYMD = (date) => {
  if (!date) return "";
  if (typeof date === "string") return date.substring(0, 10);
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDisplayDate = (val) => {
  if (!val) return "-";
  const dateOnly = String(val).substring(0, 10);
  const parts = dateOnly.split("-");
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : dateOnly;
};

const renderOrderNumber = (val, isMobile) => {
  if (!val) return "-";
  const str = String(val);
  if (str.length <= 4) {
    return <span style={{ fontWeight: 800, color: "#000" }}>{str}</span>;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
      <span style={{ color: "#666", fontSize: isMobile ? "0.75rem" : "0.85rem" }}>
        {str.slice(0, -4)}
      </span>
      <span style={{ fontWeight: 800, color: "#000", fontSize: isMobile ? "0.75rem" : "0.85rem" }}>
        {str.slice(-4)}
      </span>
    </div>
  );
};

const statusOptions = [
  { value: "all", label: "All Status" },
  { value: "order_placed", label: "Order Placed" },
  { value: "preparing", label: "Preparing" },
  { value: "ready_for_delivery", label: "Ready for Delivery" },
  { value: "completed", label: "Completed" },
];

const badgeColors = {
  order_placed: "#1976d2",
  preparing: "#7c0bcc",
  ready_for_delivery: "#e65100",
  completed: "#2e7d32",
  cancelled: "#d32f2f",
  refunded: "#9c27b0",
};

const useDebounce = (value, delay = 400) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const KitchenReports = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const showSnackbarRef = React.useRef(showSnackbar);
  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  // Loading & Data States
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [summary, setSummary] = useState({
    total_products: 0,
    total_quantity: 0,
    sweet_quantity: 0,
    cake_quantity: 0,
    total_orders: 0,
    total_branches: 0,
  });

  // Tab State: 0 = All, 1 = Sweet Factory, 2 = Cake Factory
  const [currentTab, setCurrentTab] = useState(0);

  // Filter States
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [dateType, setDateType] = useState("delivery_date"); // 'all', 'delivery_date', or 'order_date'
  const [datePreset, setDatePreset] = useState("today"); // today, tomorrow, yesterday, custom, all
  const [fromDate, setFromDate] = useState(getTodayDate());
  const [toDate, setToDate] = useState(getTodayDate());
  const [sortBy, setSortBy] = useState("recent"); // 'recent' (newest first) or 'quantity' (highest qty first)

  // Pagination Model for DataGrid
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // Drilldown Dialog Modal State
  const [selectedItemOrders, setSelectedItemOrders] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch Report Data from API
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        department: currentTab === 1 ? "sweet" : currentTab === 2 ? "cake" : "all",
        date_type: dateType,
        sort_by: sortBy,
      };

      if (datePreset === "all") {
        params.date_preset = "all";
      } else if (datePreset === "today") {
        params.date = formatDateToYMD(getTodayDate());
      } else if (datePreset === "tomorrow") {
        params.date = formatDateToYMD(getTomorrowDate());
      } else if (datePreset === "yesterday") {
        params.date = formatDateToYMD(getYesterdayDate());
      } else if (datePreset === "custom") {
        if (fromDate) params.from_date = formatDateToYMD(fromDate);
        if (toDate) params.to_date = formatDateToYMD(toDate);
      }

      if (selectedBranch !== "all") {
        params.branch_id = selectedBranch;
      }

      if (selectedStatus !== "all") {
        params.status = selectedStatus;
      }

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      const response = await getKitchenReports(params);
      const res = response?.data;
      if (res && res.status === "success") {
        setReportData(res.data || []);
        if (res.summary) {
          setSummary(res.summary);
        }
        if (res.branches && res.branches.length > 0) {
          setBranches(res.branches);
        }
      } else {
        showSnackbarRef.current?.(res?.message || "Failed to retrieve kitchen report", "error");
      }
    } catch (err) {
      console.error("Kitchen report fetch error:", err);
      showSnackbarRef.current?.("Error fetching kitchen report data", "error");
    } finally {
      setLoading(false);
    }
  }, [
    currentTab,
    dateType,
    datePreset,
    fromDate,
    toDate,
    selectedBranch,
    selectedStatus,
    debouncedSearch,
    sortBy,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Handle Date Preset Change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    if (preset === "today") {
      setFromDate(getTodayDate());
      setToDate(getTodayDate());
    } else if (preset === "tomorrow") {
      setFromDate(getTomorrowDate());
      setToDate(getTomorrowDate());
    } else if (preset === "yesterday") {
      setFromDate(getYesterdayDate());
      setToDate(getYesterdayDate());
    }
  };

  // Reset Filters
  const handleReset = () => {
    setSearch("");
    setSelectedBranch("all");
    setSelectedStatus("all");
    setDateType("delivery_date");
    setDatePreset("today");
    setFromDate(getTodayDate());
    setToDate(getTodayDate());
    setSortBy("recent");
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  // Export Consolidated Data to CSV
  const handleExport = () => {
    if (!reportData || reportData.length === 0) {
      showSnackbar("No data available to export", "warning");
      return;
    }

    const headers = [
      "Item Name",
      "Short Code",
      "Category",
      "Department",
      "Consolidated Total Quantity",
      "Unit",
      "Total Orders",
      "Branch Breakdown",
    ];

    const rows = reportData.map((item) => {
      const branchSummary = (item.branch_breakdown || [])
        .map((b) => `${b.branch_name} (${b.branch_code || "B"}): ${b.quantity} ${item.units}`)
        .join(" | ");

      return [
        `"${(item.product_name || "").replace(/"/g, '""')}"`,
        `"${item.short_code || ""}"`,
        `"${(item.category_name || "").replace(/"/g, '""')}"`,
        `"${item.department || ""}"`,
        `"${item.total_quantity || 0}"`,
        `"${item.units || ""}"`,
        `"${item.order_count || 0}"`,
        `"${branchSummary.replace(/"/g, '""')}"`,
      ].join(",");
    });

    const csvContent = headers.join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const dateLabel = datePreset === "custom" ? `${formatDateToYMD(fromDate)}_to_${formatDateToYMD(toDate)}` : datePreset;
    link.setAttribute("download", `kitchen_reports_${dateLabel}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showSnackbar("Kitchen report exported successfully!", "success");
  };

  // Open Orders Drill-down Dialog
  const handleOpenOrdersDialog = (item) => {
    setSelectedItemOrders(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItemOrders(null);
  };

  // DataGrid Columns Definition
  const columns = useMemo(
    () => [
      {
        field: "product_name",
        headerName: "Product",
        flex: 1.2,
        minWidth: isMobile ? 140 : 180,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", py: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "0.80rem" : "0.88rem",
                color: "#1e293b",
                lineHeight: 1.2,
                textAlign: "center",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: "category_name",
        headerName: "Category",
        flex: 0.9,
        minWidth: isMobile ? 110 : 130,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%", py: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.82rem",
                color: "#475569",
                textAlign: "center",
              }}
            >
              {params.value || "General"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "department",
        headerName: "Department",
        flex: 0.9,
        minWidth: isMobile ? 130 : 150,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const isCake = params.value === "Cake Factory";
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
              <Chip
                icon={
                  isCake ? (
                    <CakeIcon sx={{ fontSize: "16px !important", color: "#ec4899 !important" }} />
                  ) : (
                    <FactoryIcon sx={{ fontSize: "16px !important", color: "#7c0bcc !important" }} />
                  )
                }
                label={isCake ? "Cake Factory" : "Sweet Factory"}
                size="small"
                sx={{
                  backgroundColor: isCake ? "rgba(236, 72, 153, 0.12)" : "rgba(124, 11, 204, 0.12)",
                  color: isCake ? "#be185d" : "#7c0bcc",
                  fontWeight: 700,
                  fontSize: isMobile ? "0.68rem" : "0.75rem",
                  borderRadius: "8px",
                  height: "26px",
                }}
              />
            </Box>
          );
        },
      },
      {
        field: "total_quantity",
        headerName: "Consolidated Qty",
        flex: 0.9,
        minWidth: isMobile ? 120 : 140,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const row = params.row;
          return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
              <Box
                sx={{
                  bgcolor: "rgba(124, 11, 204, 0.08)",
                  border: "1px solid rgba(124, 11, 204, 0.2)",
                  borderRadius: "8px",
                  px: 1.2,
                  py: 0.3,
                  display: "inline-flex",
                  alignItems: "baseline",
                  gap: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: isMobile ? "0.85rem" : "0.95rem",
                    color: Color.secondary.main,
                  }}
                >
                  {params.value}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b" }}>
                  {row.units}
                </Typography>
              </Box>
            </Box>
          );
        },
      },
      // {
      //   field: "branch_breakdown",
      //   headerName: "Branch Breakdown",
      //   flex: 1.6,
      //   minWidth: isMobile ? 200 : 280,
      //   headerAlign: "center",
      //   align: "center",
      //   renderCell: (params) => {
      //     const branchesList = params.value || [];
      //     if (branchesList.length === 0) {
      //       return (
      //         <Box
      //           sx={{
      //             display: "flex",
      //             alignItems: "center",
      //             justifyContent: "center",
      //             width: "100%",
      //             height: "100%",
      //           }}
      //         >
      //           <Typography variant="caption" color="text.secondary">
      //             -
      //           </Typography>
      //         </Box>
      //       );
      //     }
      //     return (
      //       <Box
      //         sx={{
      //           display: "flex",
      //           alignItems: "center",
      //           justifyContent: "center",
      //           alignContent: "center",
      //           flexWrap: "wrap",
      //           gap: 0.6,
      //           py: 0.5,
      //           width: "100%",
      //           height: "100%",
      //           textAlign: "center",
      //         }}
      //       >
      //         {branchesList.map((b) => (
      //           <Tooltip
      //             key={b.branch_id}
      //             title={`${b.branch_name}: ${b.quantity} ${params.row.units} across ${b.order_count} order(s)`}
      //           >
      //             <Chip
      //               label={
      //                 <span>
      //                   <strong>{b.branch_name}</strong>: {b.quantity} {params.row.units}
      //                 </span>
      //               }
      //               size="small"
      //               sx={{
      //                 backgroundColor: "#f1f5f9",
      //                 border: "1px solid #e2e8f0",
      //                 color: "#334155",
      //                 fontWeight: 500,
      //                 fontSize: "0.72rem",
      //                 height: 24,
      //               }}
      //             />
      //           </Tooltip>
      //         ))}
      //       </Box>
      //     );
      //   },
      // },
      {
        field: "order_count",
        headerName: "Orders",
        flex: 0.7,
        minWidth: isMobile ? 85 : 100,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
            <Chip
              label={`${params.value || 0} order${(params.value || 0) === 1 ? "" : "s"}`}
              size="small"
              sx={{
                backgroundColor: "rgba(2, 132, 199, 0.1)",
                color: "#0369a1",
                fontWeight: 600,
                fontSize: "0.72rem",
                borderRadius: "8px",
                height: "24px",
              }}
            />
          </Box>
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.8,
        minWidth: isMobile ? 100 : 120,
        headerAlign: "center",
        align: "center",
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<VisibilityIcon sx={{ fontSize: "16px !important" }} />}
              onClick={() => handleOpenOrdersDialog(params.row)}
              sx={{
                background: Color.secondary.main,
                color: "white",
                height: "28px",
                fontSize: "0.75rem",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "6px",
                boxShadow: "none",
                "&:hover": {
                  background: Color.secondary.dark || "#6509a7",
                  boxShadow: "none",
                },
              }}
            >
              View
            </Button>
          </Box>
        ),
      },
    ],
    [isMobile]
  );

  // Drilldown orders sorted by most recent first
  const modalOrders = useMemo(() => {
    if (!selectedItemOrders?.orders) return [];
    return [...selectedItemOrders.orders].sort((a, b) => {
      const timeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const timeB = b.created_at ? new Date(b.created_at).getTime() : 0;
      if (timeB !== timeA) return timeB - timeA;
      return (b.order_id || 0) - (a.order_id || 0);
    });
  }, [selectedItemOrders]);

  return (
    <Box mb={{ xs: 2, sm: 2, md: 4, lg: 4 }} sx={{ width: "100%", padding: isMobile ? 1 : 0 }}>
      {/* Category / Department Tabs (Standard SVM UI Style) */}
      <Box sx={{ borderBottom: "1px solid #e0e0e0", pb: 1, width: "100%", mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, val) => {
            setCurrentTab(val);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: "37px",
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTabs-flexContainer": {
              justifyContent: { xs: "center", md: "flex-start" },
              width: "100%",
            },
            "& .MuiTab-root": {
              flex: { xs: 1, sm: "initial" },
              textTransform: "none",
              fontWeight: 600,
              fontSize: isMobile ? "0.75rem" : "0.9rem",
              minWidth: isMobile ? "auto" : 160,
              borderRadius: "8px",
              minHeight: "37px",
              height: "37px",
              padding: isMobile ? "4px 10px" : "0px 20px",
              marginRight: { xs: "4px", sm: "12px" },
              "&:last-of-type": {
                marginRight: 0,
              },
              backgroundColor: "#ffffff",
              color: "#666",
              border: "1px solid #e0e0e0",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: "#f8f9fa",
                borderColor: Color.secondary.main,
              },
            },
            "& .Mui-selected": {
              backgroundColor: `${Color.secondary.main} !important`,
              color: "#ffffff !important",
              borderColor: Color.secondary.main,
              boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
            },
          }}
        >
          <Tab icon={<ReportIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="All Kitchen Production" />
          <Tab icon={<FactoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Sweet Factory" />
          <Tab icon={<CakeIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Cake Factory" />
        </Tabs>
      </Box>

      {/* Filter Section (Exact standard box from Reports.js) */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          border: "1px solid lightgray",
          borderRadius: 2,
          backgroundColor: "#fafafa",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          {/* Search Input */}
          <Grid item xs={12} sm={12} md={12} lg={12}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by product name, code, category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "gray", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  fontSize: "0.85rem",
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
              }}
            />
          </Grid>

          {/* Branch Filter */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <FormControl size="small" fullWidth>
              <Select
                value={selectedBranch}
                onChange={(e) => {
                  setSelectedBranch(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                displayEmpty
                sx={{ height: 37, fontSize: "0.80rem", backgroundColor: "white", borderRadius: "8px" }}
              >
                <MenuItem value="all">All Branches</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id} sx={{ fontSize: "0.80rem" }}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Order Status Filter */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <FormControl size="small" fullWidth>
              <Select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                displayEmpty
                sx={{ height: 37, fontSize: "0.80rem", backgroundColor: "white", borderRadius: "8px" }}
              >
                {statusOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.80rem" }}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Date Type Filter (Delivery Date vs Order Date vs All) */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <FormControl size="small" fullWidth>
              <Select
                value={dateType}
                onChange={(e) => {
                  setDateType(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                displayEmpty
                sx={{ height: 37, fontSize: "0.80rem", backgroundColor: "white", borderRadius: "8px" }}
              >
                <MenuItem value="all">All Dates</MenuItem>
                <MenuItem value="delivery_date">Delivery Date</MenuItem>
                <MenuItem value="order_date">Order Date</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Date Preset Filter */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <FormControl size="small" fullWidth>
              <Select
                value={datePreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                displayEmpty
                sx={{ height: 37, fontSize: "0.80rem", backgroundColor: "white", borderRadius: "8px" }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="tomorrow">Tomorrow</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="custom">Custom Date</MenuItem>
                <MenuItem value="all">All Dates</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* From Date Picker */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={fromDate}
                onChange={(newValue) => {
                  setFromDate(newValue);
                  if (datePreset !== "custom") setDatePreset("custom");
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "From Date",
                    sx: {
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        height: 37,
                        fontSize: "0.80rem",
                        paddingRight: "4px",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 4px 8px 8px",
                      },
                      "& .MuiIconButton-root": {
                        padding: "4px",
                        marginRight: "-2px",
                      },
                    },
                  },
                }}
                format="yyyy-MM-dd"
              />
            </LocalizationProvider>
          </Grid>

          {/* To Date Picker */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={toDate}
                onChange={(newValue) => {
                  setToDate(newValue);
                  if (datePreset !== "custom") setDatePreset("custom");
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "To Date",
                    sx: {
                      backgroundColor: "white",
                      "& .MuiOutlinedInput-root": {
                        height: 37,
                        fontSize: "0.80rem",
                        paddingRight: "4px",
                        borderRadius: "8px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 4px 8px 8px",
                      },
                      "& .MuiIconButton-root": {
                        padding: "4px",
                        marginRight: "-2px",
                      },
                    },
                  },
                }}
                format="yyyy-MM-dd"
              />
            </LocalizationProvider>
          </Grid>

          {/* Sort By Filter */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <FormControl size="small" fullWidth>
              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                displayEmpty
                sx={{ height: 37, fontSize: "0.80rem", backgroundColor: "white", borderRadius: "8px" }}
              >
                <MenuItem value="recent" sx={{ fontSize: "0.80rem" }}>
                  Most Recent First
                </MenuItem>
                <MenuItem value="quantity" sx={{ fontSize: "0.80rem" }}>
                  Highest Quantity First
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Reset Button */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Button
              onClick={handleReset}
              size="small"
              fullWidth
              sx={{
                height: "37px !important",
                border: `1px solid ${Color.secondary.main}`,
                color: Color.secondary.main,
                fontSize: "0.80rem",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": {
                  borderColor: Color.secondary.dark,
                  backgroundColor: "rgba(124, 11, 204, 0.04)",
                },
              }}
            >
              Reset
            </Button>
          </Grid>

          {/* Export Button */}
          <Grid item xs={12} sm={6} md={3} lg={2.4}>
            <Button
              onClick={handleExport}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{
                background: Color.primary.main,
                color: "white",
                height: "37px !important",
                fontSize: "0.80rem",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { background: Color.primary.dark },
              }}
            >
              Export
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Table Section: DataGrid (Exact standard UI across svm-ui) */}
      <Box sx={{ width: "100%", mt: 2 }}>
        {reportData.length === 0 && !loading && (
          <Box sx={{ textAlign: "center", p: 4, bgcolor: "#fff", borderRadius: "10px", border: "1px solid #e0e0e0" }}>
            <Typography sx={{ fontSize: isMobile ? "0.9rem" : "1rem", color: "text.secondary" }}>
              No kitchen production records found for the selected filters.
            </Typography>
          </Box>
        )}

        <DataGrid
          rows={reportData}
          columns={columns}
          rowHeight={isMobile ? 54 : 64}
          loading={loading}
          getRowId={(row) => row.id || row.product_id}
          rowCount={reportData.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
          onRowClick={(params) => handleOpenOrdersDialog(params.row)}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(124, 11, 204, 0.05) !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "15px",
              fontWeight: 600,
              px: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: 400,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
          }}
        />
      </Box>

      {/* Drill-down Orders Dialog Modal */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", p: 1 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight={700} color="#1e293b">
                {selectedItemOrders?.product_name}
              </Typography>
              <Typography variant="caption" color="#64748b">
                Total Production:{" "}
                <strong>
                  {selectedItemOrders?.total_quantity} {selectedItemOrders?.units}
                </strong>{" "}
                across {modalOrders.length} order(s) (Newest orders first)
              </Typography>
            </Box>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 460, overflowX: "auto" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>ORDER #</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>PRODUCT NAME</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>BRANCH</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>CUSTOMER</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }} align="right">
                    QTY ({selectedItemOrders?.units || ""})
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>ORDER DATE</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>DELIVERY DATE</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }} align="center">
                    STATUS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3, whiteSpace: "nowrap" }}>
                      <Typography variant="body2" color="text.secondary">
                        No individual orders found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  modalOrders.map((o, idx) => {
                    const status = o.status || "order_placed";
                    const statusLabel =
                      statusOptions.find((opt) => opt.value === status)?.label ||
                      status.replace(/_/g, " ");
                    const bg = badgeColors[status] || "#757575";

                    return (
                      <TableRow key={idx} hover sx={{ "&:nth-of-type(odd)": { backgroundColor: "#f9fafb" } }}>
                        <TableCell sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                          {renderOrderNumber(o.order_number, isMobile)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {o.product_name || selectedItemOrders?.product_name || "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {o.branch_name}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                          {o.customer_name || "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.85rem", fontWeight: 700, color: Color.secondary.main, whiteSpace: "nowrap" }} align="right">
                          {o.quantity}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
                          {formatDisplayDate(o.order_date)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>
                          {formatDisplayDate(o.delivery_date)}
                        </TableCell>
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Chip
                            label={statusLabel.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: bg,
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              borderRadius: "6px",
                              height: "22px",
                              whiteSpace: "nowrap",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: Color.secondary.main,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KitchenReports;
